import { FunctionComponent, useMemo, useState } from 'react';
import TextField from '../../components/Fields/TextField/TextField';
import SubmitButton from '../../components/SubmitButton/SubmitButton';
import LinkParagraph from '../../components/LinkParagraph/LinkParagraph';
import I18n from '../../components/I18n/I18n';
import SupportModal from '../../components/SupportModal/SupportModal';
import { IPage } from '../../components/PageWrapper/types';
import useIdentityForm from '../../common/hooks/UseIdentityForm';
import useIdentityParams from '../../common/hooks/Parameters/UseIdentityParams';
import usePageAccessGuard from '../../common/hooks/UsePageAccessGuard';
import useTracking from '../../common/hooks/UseTracking';
import useIdentityNavigation from '../../common/hooks/UseIdentityNavigation';
import useAPI from '../../common/hooks/UseAPI';
import { asI18nKey } from '../../common/utilities/Localization';
import { ITwoFactorResponse } from './types';
import { DeviceAddress, Instructions, OtherOptionsContainer } from './TwoFactorPage.style';
import { sendCode, verifyCode } from './TwoFactorService';
import TimedResendButton from '../../components/TimedResendButton/TimedResendButton';
import useTrigger from '../../common/hooks/UseTrigger';
import { ServerError } from '../../common/utilities/API/types';
import { AlertType } from '../../components/AlertToast/types';

const TwoFactorPage: FunctionComponent<IPage> = props => {
    const { setInput, formInputs, inputsVerificationTrigger, submissionTrigger, logSubmissionFailure } = useIdentityForm();
    const [supportModalOpen, setSupportModalOpen] = useState<boolean>(false);
    const { trackEvent } = useTracking(props.trackingCategory);
    const { navigate, redirectToReturnUrl } = useIdentityNavigation();
    const api = useAPI();
    const cancelResendCooldown = useTrigger();
    const { allParams } = useIdentityParams<ITwoFactorResponse>();
    const [currentProviderData, setCurrentProviderData] = useState<ITwoFactorResponse>(allParams);
    const defaultProvider = useMemo(() => allParams.twofactormethod?.provider, [allParams]);
    const providerName = useMemo(() => currentProviderData.twofactormethod?.provider?.toString()?.toLowerCase(), [currentProviderData]);
    const otherProviderName = useMemo(() => {
      return currentProviderData.othertwofactormethod?.provider?.toString()?.toLowerCase()
    }, [currentProviderData]);

    usePageAccessGuard(!!allParams.signedin, {
      callback: () => {
        redirectToReturnUrl();
        props.disablePage();
      },
      fallbackTarget: null
    });

    usePageAccessGuard(!currentProviderData.twofactormethod?.provider || !currentProviderData.twofactormethod?.deviceAddress);

    /**
     * Send the device verification code to the user.
     */
    const resendCode = async (switchMethod: boolean = false): Promise<void> => {
        trackEvent('resend email/link', 'click');

        let methodToUse = switchMethod ? currentProviderData.othertwofactormethod?.provider : currentProviderData.twofactormethod?.provider;
        await api(() => sendCode(methodToUse !== defaultProvider ? methodToUse : undefined), {
            errorsMap: () => [
                {
                    error: ServerError.CodeIssueFailure,
                    message: props.translate(asI18nKey('2fa.error.invalid_request'))
                }
            ],
            onSuccess: () => {
                if (switchMethod) {
                    setCurrentProviderData({
                        twofactormethod: currentProviderData.othertwofactormethod,
                        othertwofactormethod: currentProviderData.twofactormethod
                    });
                }
            },
            onError: ({ message, forceRefresh, mappedHandler }) => {
                if (!!message && mappedHandler) {
                    let alertType = mappedHandler.alertType || AlertType.Error;
                    props.onServerAlert(message, alertType);
                    logSubmissionFailure(message);
                }
                else props.onUnexpectedError(forceRefresh);

                cancelResendCooldown.fire();
            }
        });
    }

    /**
     * Activate when the page's form is submitted.
     * This function verifies the user's code and continues with the login flow.
     *
     * @param {string} code - The user's typed code
     * @returns {Promise<boolean>} True if the submission is successful.
     */
    const onSubmit = async ({ code }: Record<string, string>): Promise<boolean> => {
        return await api(() => verifyCode(code), {
            errorsMap: params => [
                {
                    error: ServerError.CodeVerificationRejected,
                    message: props.translate(asI18nKey('2fa.error.invalid_code'))
                },
                {
                    error: ServerError.UserLocked,
                    message: props.translate(asI18nKey('2fa.error.user_locked'), { minutes: params?.minutesLeft || 5 }),
                    alertType: AlertType.Warning
                }
            ],
            onSuccess: async ({ status, data }) => {
                switch (status) {
                    case 200:
                        props.disablePage();
                        await redirectToReturnUrl();
                        break;

                    case 211:
                        navigate('ResetPassword', undefined, data);
                        break;

                    case 212:
                        navigate('Lockout');
                        break;
                }
            },
            onError: ({ message, forceRefresh, mappedHandler }) => {
                if (!!message && mappedHandler) {
                    let alertType = mappedHandler.alertType || AlertType.Error;
                    props.onServerAlert(message, alertType);
                    logSubmissionFailure(message);
                }
                else props.onUnexpectedError(forceRefresh);
            }
        });
    }

    return (
        <>
            <Instructions>
                <I18n>
                    2fa.subtitle.{providerName}
                </I18n>
                <DeviceAddress>
                    &nbsp;
                    {!!currentProviderData.twofactormethod?.deviceAddress ? (
                       <span>{currentProviderData.twofactormethod?.deviceAddress}</span>
                    ) : (
                        <span>
                            <I18n>2fa.subtitle.your_device</I18n>
                        </span>
                    )}
                </DeviceAddress>
            </Instructions>
            <TextField
                name={'code'}
                type={'Text'}
                label={props.translate(asI18nKey('2fa.field.code.label'))}
                required
                focusOnPageLoad
                shouldVerify={inputsVerificationTrigger.active}
                trackingCategory={props.trackingCategory}
                onChange={setInput}
                onEnterPress={submissionTrigger.fire}
                automationName={'code'}
                {...(!!allParams.code ? { initialValue: allParams.code } : {})}
            />
            <SubmitButton
                onSuccess={onSubmit}
                formInputs={formInputs}
                onFailedVerification={inputsVerificationTrigger.fire}
                trackingCategory={props.trackingCategory}
                trackingComponentName={'continue'}
                shouldSubmit={submissionTrigger.active}
                automationName={'submit-button'}
            >
                <I18n>2fa.button</I18n>
            </SubmitButton>
            <TimedResendButton
                onSendCode={() => resendCode(false)}
                confirmationMessage={props.translate(asI18nKey('2fa.no_code.confirmation'))}
                buttonLabel={props.translate(asI18nKey(`2fa.no_code.action`))}
                buttonPrefix={props.translate(asI18nKey('2fa.no_code'))}
                timerPrefix={props.translate(asI18nKey('2fa.no_code.cooldown.prefix'))}
                timerSuffix={props.translate(asI18nKey('2fa.no_code.cooldown.suffix'))}
                cancelCooldown={cancelResendCooldown.active}
                cooldownTime={30}
            />
            <OtherOptionsContainer>
                {otherProviderName && (
                    <LinkParagraph
                        automationName='2fa_method_link'
                        suffix={props.translate(asI18nKey('2fa.method.switch'))}
                        linkLabel={props.translate(asI18nKey(`2fa.method.${otherProviderName}`)) || ''}
                        callback={() => resendCode(true)}
                    />
                )}
                <LinkParagraph
                    automationName='2fa_support_link'
                    prefix={props.translate(asI18nKey(otherProviderName ? '2fa.method.switch.support': '2fa.support'))}
                    linkLabel={props.translate(asI18nKey('2fa.support.action'))}
                    callback={() => setSupportModalOpen(true)}
                />
            </OtherOptionsContainer>
            <SupportModal
                isOpen={supportModalOpen}
                onClose={() => setSupportModalOpen(false)}
            />
        </>
    );
}

export default TwoFactorPage;
